import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface EvaluationUsage {
  evaluations_used: number;
  max_evaluations: number;
  usage_percentage: number;
  can_create_tests: boolean;
  last_updated: string | null;
  last_purchased: string | null;
}

export function useEvaluationUsage() {
  const { user } = useAuth();
  const [usage, setUsage] = useState<EvaluationUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = async () => {
    if (!user) {
      setUsage(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_evaluation_usage')
        .select('evaluations_used, max_evaluations, updated_at, last_purchased')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      // If no record exists, create one with default values
      if (!data) {
        const { data: newRecord, error: insertError } = await supabase
          .from('user_evaluation_usage')
          .insert({
            user_id: user.id,
            evaluations_used: 0,
            max_evaluations: 1000
          })
          .select('evaluations_used, max_evaluations, updated_at, last_purchased')
          .single();

        if (insertError) {
          throw insertError;
        }

        const usageData = {
          evaluations_used: newRecord.evaluations_used,
          max_evaluations: newRecord.max_evaluations,
          usage_percentage: 0,
          can_create_tests: true,
          last_updated: newRecord.updated_at,
          last_purchased: newRecord.last_purchased
        };

        setUsage(usageData);
        return;
      }

      const usagePercentage = (data.evaluations_used / data.max_evaluations) * 100;
      const canCreateTests = data.evaluations_used < data.max_evaluations;

      setUsage({
        evaluations_used: data.evaluations_used,
        max_evaluations: data.max_evaluations,
        usage_percentage: usagePercentage,
        can_create_tests: canCreateTests,
        last_updated: data.updated_at,
        last_purchased: data.last_purchased
      });

    } catch (err) {
      console.error('Error fetching evaluation usage:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch usage data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [user]);

  return {
    usage,
    isLoading,
    error,
    refetch: fetchUsage
  };
}